import React, { useState } from 'react';
import { ethers } from 'ethers';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';
import CopyButton from '../common/CopyButton';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { formatTimestamp, formatUsdt } from '../../utils/format';
import { shortenAddress } from '../../utils/wallet';
import { TOTAL_PACKAGES, PACKAGE_PRICES } from '../../config/contracts';

// Search a user by ID or wallet → view full profile + admin actions (block/unblock, update wallet)
const UserLookup = () => {
  const { storage } = useContracts();
  const notify = useNotify();

  const [query, setQuery] = useState('');
  const [profile, setProfile] = useState(null);
  const [packages, setPackages] = useState([]);
  const [income, setIncome] = useState(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(null);
  const [walletEdit, setWalletEdit] = useState('');

  const search = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true); setProfile(null); setPackages([]); setIncome(null);
    try {
      let id;
      if (ethers.utils.isAddress(q)) {
        const uid = await storage.addressToId(q);
        id = uid.toNumber ? uid.toNumber() : Number(uid);
      } else {
        id = parseInt(q, 10);
      }
      if (!id) { notify.error('User not found'); setSearching(false); return; }

      const [u, bd, ...pkgs] = await Promise.all([
        storage.users(id),
        storage.userIncomeBreakdown(id),
        ...Array.from({ length: TOTAL_PACKAGES }, (_, i) => storage.userPackages(id, i + 1))
      ]);

      const numId = u[1].toNumber ? u[1].toNumber() : Number(u[1]);
      if (numId === 0) { notify.error(`User not found: ${q}`); setSearching(false); return; }

      setProfile({
        id: numId,
        wallet: u[0],
        referrerId: u[2].toNumber ? u[2].toNumber() : Number(u[2]),
        serialNumber: u[3].toNumber ? u[3].toNumber() : Number(u[3]),
        joinTime: u[4].toNumber ? u[4].toNumber() : Number(u[4]),
        isBlocked: u[5],
        registrationType: u[6],
        totalDirectReferrals: u[7].toNumber ? u[7].toNumber() : Number(u[7]),
        topPackageId: u[8].toNumber ? u[8].toNumber() : Number(u[8]),
        totalClaimed: u[9],
        totalIncome: u[10]
      });
      setIncome({
        direct: bd[0], level: bd[1], royalty: bd[2]
      });
      setPackages(pkgs.map((p) => ({
        isActive: p[0],
        activationTime: p[1],
        totalInvested: p[2],
        totalEarned: p[3],
        ceilingLimit: p[4]
      })));
      setWalletEdit('');
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setSearching(false);
    }
  };

  const doTx = async (label, promiseFn, refresh = true) => {
    setBusy(label);
    const loadingId = notify.loading(`${label}...`);
    try {
      const tx = await promiseFn();
      notify.dismiss(loadingId);
      const mining = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(mining);
      notify.success(`${label} — done`);
      if (refresh) await search();
    } catch (err) {
      notify.dismiss(loadingId);
      notify.error(parseError(err));
    } finally {
      setBusy(null);
    }
  };

  const toggleBlock = () => {
    if (!profile) return;
    doTx(
      profile.isBlocked ? 'Unblocking user' : 'Blocking user',
      () => profile.isBlocked ? storage.unblockUser(profile.id) : storage.blockUser(profile.id)
    );
  };

  const updateWallet = () => {
    if (!ethers.utils.isAddress(walletEdit)) { notify.error('Invalid wallet'); return; }
    doTx('Updating wallet', () => storage.updateUserWallet(profile.id, walletEdit));
  };

  return (
    <Card title="User Lookup" subtitle="Search by User ID or wallet address" glow>
      <div className="flex gap-sm mb-md">
        <input
          className="input"
          placeholder="User ID (e.g. 1234) or wallet address 0x..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()}
        />
        <button className="btn btn-primary btn-sm" onClick={search} disabled={searching || !query.trim()}>
          {searching ? <MiniLoader /> : 'Search'}
        </button>
      </div>

      {profile && (
        <div className="user-lookup-result">
          {/* Profile card */}
          <div className="lookup-header">
            <div className="lookup-avatar">
              {String(profile.id).charAt(0)}
            </div>
            <div className="flex-1">
              <div className="lookup-name">
                User #{profile.id}
                {profile.isBlocked && <span className="badge badge-error" style={{marginLeft:8}}>Blocked</span>}
              </div>
              <div className="lookup-sub">
                ID {profile.id} · Serial {profile.serialNumber} · Ref {profile.referrerId}
              </div>
              <div className="flex items-center gap-sm mt-sm">
                <span className="font-mono text-muted" style={{ fontSize: 12 }}>
                  {shortenAddress(profile.wallet, 6)}
                </span>
                <CopyButton text={profile.wallet} label="Copy" />
              </div>
            </div>
          </div>

          <div className="lookup-grid mt-md">
            <MiniStat label="Top Package" value={profile.topPackageId > 0 ? `P${profile.topPackageId}` : 'None'} />
            <MiniStat label="Directs" value={profile.totalDirectReferrals} />
            <MiniStat label="Total Income" value={`${formatUsdt(profile.totalIncome)} USDT`} />
            <MiniStat label="Withdrawn" value={`${formatUsdt(profile.totalClaimed)} USDT`} />
          </div>

          {income && (
            <div className="lookup-grid mt-sm">
              <MiniStat label="Direct" value={`${formatUsdt(income.direct)} USDT`} accent="cyan" />
              <MiniStat label="Level" value={`${formatUsdt(income.level)} USDT`} accent="violet" />
              <MiniStat label="Royalty" value={`${formatUsdt(income.royalty)} USDT`} accent="magenta" />
              <MiniStat label="Joined" value={formatTimestamp(profile.joinTime).split(',')[0]} />
            </div>
          )}

          {/* Package status pills */}
          <div className="lookup-section-title">Package Status</div>
          <div className="pkg-status-grid">
            {packages.map((p, i) => (
              <div key={i} className={`pkg-status ${p.isActive ? 'active' : ''}`}>
                <div className="pkg-status-num">P{i + 1}</div>
                <div className="pkg-status-price">${PACKAGE_PRICES[i]}</div>
                {p.isActive && (
                  <div className="pkg-status-earned">
                    {formatUsdt(p.totalEarned, 1)} / {formatUsdt(p.ceilingLimit, 0)}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Admin Actions */}
          <div className="lookup-section-title">Admin Actions</div>

          <div className="admin-action-row">
            <div className="flex-1">
              <div style={{ fontSize: 13, fontWeight: 600 }}>Block Status</div>
              <div className="text-muted" style={{ fontSize: 11 }}>
                {profile.isBlocked ? 'User is blocked — cannot receive income' : 'User is active'}
              </div>
            </div>
            <button
              className={profile.isBlocked ? 'btn btn-success btn-sm' : 'btn btn-error btn-sm'}
              onClick={toggleBlock}
              disabled={busy !== null}
            >
              {busy && busy.includes('lock') ? <MiniLoader /> : (profile.isBlocked ? 'Unblock' : 'Block')}
            </button>
          </div>

          <div className="admin-action-block">
            <div className="input-label mb-sm">Update Wallet</div>
            <div className="flex gap-sm">
              <input className="input font-mono" placeholder="0x..." value={walletEdit} onChange={(e) => setWalletEdit(e.target.value)} />
              <button className="btn btn-primary btn-sm" onClick={updateWallet} disabled={busy !== null || !walletEdit}>
                {busy === 'Updating wallet' ? <MiniLoader /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

const MiniStat = ({ label, value, accent }) => (
  <div className={'lookup-stat ' + (accent ? `accent-${accent}` : '')}>
    <div className="lookup-stat-label">{label}</div>
    <div className="lookup-stat-value">{value}</div>
  </div>
);

export default UserLookup;
